import React, { useState, useEffect } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { getLanguageDirection } from '../../i18n/config';
import LanguageSwitcherGuest from '../common/LanguageSwitcherGuest';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Loader2 } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';

const Signup: React.FC = () => {
  const { t, i18n } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [organizationName, setOrganizationName] = useState('');
  const [organizationSlug, setOrganizationSlug] = useState('');
  const [formError, setFormError] = useState('');
  const [signupMode, setSignupMode] = useState<'organization' | 'legacy'>('organization');
  const [, setIsAutofilled] = useState(false);
  const { signup, createOrganization, loading, error } = useAuth();
  const isRTL = getLanguageDirection(i18n.language) === 'rtl';

  useEffect(() => {
    const timer = setTimeout(() => {
      const emailInput = document.getElementById('email') as HTMLInputElement;
      const passwordInput = document.getElementById('password') as HTMLInputElement;
      const displayNameInput = document.getElementById('displayName') as HTMLInputElement;

      if ((emailInput && emailInput.value) ||
        (passwordInput && passwordInput.value) ||
        (displayNameInput && displayNameInput.value)) {
        setIsAutofilled(true);
        if (emailInput && emailInput.value) setEmail(emailInput.value);
        if (passwordInput && passwordInput.value) setPassword(passwordInput.value);
        if (displayNameInput && displayNameInput.value) setDisplayName(displayNameInput.value);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Auto-generate slug from organization name
  useEffect(() => {
    if (organizationName) {
      const slug = organizationName
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-|-$/g, '');
      setOrganizationSlug(slug);
    }
  }, [organizationName]);

  const handleOrganizationSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!email) {
      setFormError(t('organization.create.emailRequired'));
      return;
    }
    if (!displayName) {
      setFormError(t('organization.create.displayNameRequired'));
      return;
    }
    if (!organizationName) {
      setFormError(t('organization.create.nameRequired'));
      return;
    }
    if (!organizationSlug) {
      setFormError(t('organization.create.slugRequired'));
      return;
    }

    try {
      await createOrganization({
        organizationName,
        organizationSlug,
        email,
        name: displayName
      });
    } catch (err) {
      console.error('Error creating organization:', err);
    }
  };

  const handleLegacySignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError('');

    if (!email) {
      setFormError(t('errors.validation.emailRequired'));
      return;
    }
    if (!password) {
      setFormError(t('errors.validation.passwordRequired'));
      return;
    }
    if (password !== confirmPassword) {
      setFormError(t('errors.validation.passwordsDoNotMatch'));
      return;
    }
    if (password.length < 8) {
      setFormError(t('errors.validation.passwordTooShort'));
      return;
    }

    try {
      await signup({
        email,
        password,
        displayName: displayName.trim() || undefined
      });
    } catch (err) {
      console.error('Error in signup component:', err);
    }
  };

  return (
    <div className="container max-w-md mx-auto px-4" dir={getLanguageDirection(i18n.language)}>
      {/* Language Switcher */}
      <div className={`flex ${isRTL ? 'justify-start' : 'justify-end'} mb-4 mt-4`}>
        <LanguageSwitcherGuest variant="compact" showLabel={false} />
      </div>

      <Card className="mt-8 shadow-lg">
        {/* Logo */}
        <div className="flex justify-center pt-8 pb-4">
          <img
            src="/OmniLawLogoTransparent.png"
            alt={t('common.labels.omnilaw')}
            className="h-32 w-auto object-contain"
          />
        </div>

        <CardHeader className="space-y-1 pt-4">
          <CardTitle className="text-2xl font-bold text-center">
            {t('pages.signup.title')}
          </CardTitle>
        </CardHeader>

        <CardContent>
          {(error || formError) && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>
                {formError || error}
              </AlertDescription>
            </Alert>
          )}

          <Tabs value={signupMode} onValueChange={(v) => setSignupMode(v as any)} className="w-full">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="organization">{t('organization.create.title')}</TabsTrigger>
              <TabsTrigger value="legacy">{t('auth.legacy.signup')}</TabsTrigger>
            </TabsList>

            <TabsContent value="organization">
              <form onSubmit={handleOrganizationSignup} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="org-name">
                    {t('organization.create.organizationName')}
                  </Label>
                  <Input
                    id="org-name"
                    type="text"
                    value={organizationName}
                    onChange={(e) => setOrganizationName(e.target.value)}
                    disabled={loading}
                    placeholder={t('organization.create.namePlaceholder')}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="org-slug">
                    {t('organization.create.organizationSlug')}
                  </Label>
                  <Input
                    id="org-slug"
                    type="text"
                    value={organizationSlug}
                    onChange={(e) => setOrganizationSlug(e.target.value)}
                    disabled={loading}
                    placeholder={t('organization.create.slugPlaceholder')}
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    {t('organization.create.slugHelp')}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="org-email">
                    {t('organization.create.yourEmail')}
                  </Label>
                  <Input
                    id="org-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    placeholder={t('organization.create.emailPlaceholder')}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="org-display-name">
                    {t('organization.create.yourName')}
                  </Label>
                  <Input
                    id="org-display-name"
                    type="text"
                    autoComplete="name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    disabled={loading}
                    placeholder={t('organization.create.displayNamePlaceholder')}
                    required
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('organization.create.creating')}
                    </>
                  ) : (
                    t('organization.create.createButton')
                  )}
                </Button>

                <div className="text-center text-sm">
                  <RouterLink
                    to="/login"
                    className="text-gold hover:underline font-medium"
                  >
                    {t('pages.signup.signInLink')}
                  </RouterLink>
                </div>
              </form>
            </TabsContent>

            <TabsContent value="legacy">
              <form onSubmit={handleLegacySignup} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">
                    {t('pages.signup.emailLabel')}
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    name="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    disabled={loading}
                    placeholder={t('common.labels.emailExample')}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="displayName">
                    {t('pages.signup.displayNameLabel')}
                  </Label>
                  <Input
                    id="displayName"
                    type="text"
                    name="displayName"
                    autoComplete="name"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    disabled={loading}
                    placeholder={t('auth.placeholders.yourName')}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">
                    {t('pages.signup.passwordLabel')}
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    name="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    disabled={loading}
                    placeholder={t('common.labels.passwordPlaceholder')}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">
                    {t('pages.signup.confirmPasswordLabel')}
                  </Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    name="confirmPassword"
                    autoComplete="new-password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    disabled={loading}
                    placeholder="••••••••"
                    required
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('common.auth.loading')}
                    </>
                  ) : (
                    t('pages.signup.signUpButton')
                  )}
                </Button>

                <div className="text-center text-sm">
                  <RouterLink
                    to="/login"
                    className="text-gold hover:underline font-medium"
                  >
                    {t('pages.signup.signInLink')}
                  </RouterLink>
                </div>
              </form>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
};

export default Signup;
